// Thin API client. Sends the CSRF token on mutating requests and credentials
// (the session cookie) on every request.

export interface Me {
  authenticated: boolean;
  is_admin: boolean;
  sub?: string;
  email?: string;
  name?: string;
  csrf?: string;
}

export interface ChapterMeta {
  id: string;
  index: number;
  title: string;
  slug: string;
  word_count: number;
}

export interface WritingProgress {
  chapters_published: number;
  chapters_planned: number;
  total_words: number;
  percent: number;
  last_updated: string | null;
}

export interface BookView {
  book: {
    id: string;
    slug: string;
    title: string;
    subtitle: string;
    author_name: string;
    cover_image: string | null;
  };
  chapters: ChapterMeta[];
  progress: WritingProgress;
  resume: { chapter_id: string; page_in_chapter: number; percent: number } | null;
}

export interface ChapterContent {
  id: string;
  index: number;
  title: string;
  slug: string;
  blocks: any[];
}

let csrfToken: string | null = null;
export function setCsrf(token: string | null) {
  csrfToken = token;
}

async function req<T>(method: string, url: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const opts: RequestInit = { method, credentials: "same-origin", headers };
  if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const res = await fetch(url, opts);
  if (!res.ok) {
    let msg = `${res.status}`;
    try {
      const j = await res.json();
      if (j.error) msg = j.error;
    } catch {}
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

export const api = {
  me: () => req<Me>("GET", "/api/me"),
  logout: () => req("POST", "/auth/logout"),

  // public reader
  getBookList: () =>
    req<{ id: string; slug: string; title: string; subtitle: string; author_name: string; cover_image: string | null }[]>(
      "GET",
      "/api/books",
    ),
  getBook: (slug: string) => req<BookView>("GET", `/api/books/${slug}`),
  getChapter: (slug: string, cslug: string) =>
    req<ChapterContent>("GET", `/api/books/${slug}/chapters/${cslug}`),
  saveProgress: (slug: string, chapterId: string, page: number, percent: number) =>
    req("PUT", `/api/books/${slug}/progress`, {
      chapter_id: chapterId,
      page_in_chapter: page,
      percent,
    }),

  // admin
  createBook: (b: { slug: string; title: string; subtitle?: string; author_name?: string; chapters_planned?: number }) =>
    req<{ id: string; slug: string }>("POST", "/api/admin/books", b),
  updateBook: (slug: string, b: Partial<{ title: string; subtitle: string; author_name: string; chapters_planned: number; cover_image: string }>) =>
    req("PUT", `/api/admin/books/${slug}`, b),
  listChapters: (slug: string) => req<any[]>("GET", `/api/admin/books/${slug}/chapters`),
  createChapter: (slug: string, title: string) =>
    req<{ id: string; slug: string; index: number }>("POST", `/api/admin/books/${slug}/chapters`, { title }),
  getAdminChapter: (id: string) => req<ChapterContent & { status: string }>("GET", `/api/admin/chapters/${id}`),
  saveChapter: (id: string, blocks: any[], title?: string) =>
    req<{ ok: boolean; word_count: number; commit: string }>("PUT", `/api/admin/chapters/${id}`, { blocks, title }),
  publishChapter: (id: string) => req("POST", `/api/admin/chapters/${id}/publish`),
  unpublishChapter: (id: string) => req("POST", `/api/admin/chapters/${id}/unpublish`),
  deleteChapter: (id: string) => req("DELETE", `/api/admin/chapters/${id}`),
  reorder: (slug: string, order: string[]) => req("PUT", `/api/admin/books/${slug}/reorder`, { order }),
  history: (id: string) => req<{ commits: any[] }>("GET", `/api/admin/chapters/${id}/history`),
  uploadImage: async (file: File): Promise<{ src: string }> => {
    const fd = new FormData();
    fd.append("file", file);
    const headers: Record<string, string> = {};
    if (csrfToken) headers["X-CSRF-Token"] = csrfToken;
    const res = await fetch("/api/admin/upload", { method: "POST", body: fd, headers, credentials: "same-origin" });
    if (!res.ok) throw new ApiError(res.status, "upload failed");
    return res.json();
  },
};
